import { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Pause, Mic, Loader2, Copy, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';

const BARS_COUNT = 48;
const MIN_H = 2;
const MAX_H = 28;
const PLAYBACK_SPEEDS = [1, 1.5, 2];

// Verifica se a URL é cross-origin (S3 direto bloqueia fetch via CORS)
function isCrossOrigin(url) {
    try {
        const u = new URL(url, globalThis.location.href);
        return u.origin !== globalThis.location.origin;
    } catch {
        return true;
    }
}

// Extrair waveform real do áudio via decodeAudioData
async function extractWaveformFromUrl(url, count) {
    // Pular fetch para URLs cross-origin (S3 direto) — evita erros de CORS no console.
    // O <audio> element toca normalmente sem CORS; apenas fetch() é bloqueado.
    // Em localhost com Vite proxy, URLs já são same-origin (/__s3media/...).
    if (isCrossOrigin(url)) return null;

    try {
        const audioCtx = new (globalThis.AudioContext || globalThis.webkitAudioContext)();
        const response = await fetch(url);
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
        const channelData = audioBuffer.getChannelData(0);

        const samplesPerBar = Math.floor(channelData.length / count);
        const waveform = [];
        for (let i = 0; i < count; i++) {
            let sum = 0;
            const start = i * samplesPerBar;
            const end = Math.min(start + samplesPerBar, channelData.length);
            for (let j = start; j < end; j++) {
                sum += Math.abs(channelData[j]);
            }
            waveform.push(sum / samplesPerBar);
        }

        const max = Math.max(...waveform, 0.001);
        audioCtx.close();
        return waveform.map(v => v / max);
    } catch {
        return null;
    }
}

export function AudioContent({
    src,
    duration: durationProp,
    transcription,
    messageId,
    onTranscribe,
    className
}) {
    const { resolved: currentTheme } = useTheme();
    const isDark = currentTheme === 'dark';
    const isDarkRef = useRef(isDark);
    isDarkRef.current = isDark;

    const [isPlaying, setIsPlaying] = useState(false);
    const [speedIdx, setSpeedIdx] = useState(0);
    const [barHeights, setBarHeights] = useState(() => {
        return Array.from({ length: BARS_COUNT }, (_, i) => {
            const v = Math.abs(Math.sin(i * 0.45) * 0.4 + Math.sin(i * 1.1) * 0.3);
            return MIN_H + v * (MAX_H - MIN_H);
        });
    });
    const [duration, setDuration] = useState(0);
    const [transcribing, setTranscribing] = useState(false);
    const [transcribeError, setTranscribeError] = useState(null);
    const [copied, setCopied] = useState(false);
    const audioRef = useRef(null);
    const barEls = useRef([]);
    const timeEl = useRef(null);
    const progressEl = useRef(null);
    const rafId = useRef(null);

    // Extract real waveform from audio source
    useEffect(() => {
        if (!src) return;
        let cancelled = false;
        extractWaveformFromUrl(src, BARS_COUNT).then(wf => {
            if (!cancelled && wf) {
                setBarHeights(wf.map(v => MIN_H + v * (MAX_H - MIN_H)));
            }
        });
        return () => { cancelled = true; };
    }, [src]);

    const updateVisuals = useCallback((pct) => {
        // Update bar colors
        for (let i = 0; i < BARS_COUNT; i++) {
            const el = barEls.current[i];
            if (!el) continue;
            const barPct = (i / BARS_COUNT) * 100;
            el.style.backgroundColor = barPct <= pct
                ? 'hsl(142, 70%, 45%)'
                : isDarkRef.current ? 'hsla(0, 0%, 100%, 0.22)' : 'hsla(0, 0%, 0%, 0.15)';
        }
        // Update progress bar
        if (progressEl.current) {
            progressEl.current.style.width = `${pct}%`;
        }
    }, []);

    // 60fps animation loop — direct DOM, no React state
    const tickFn = useCallback(() => {
        const audio = audioRef.current;
        if (!audio || audio.paused || audio.ended) return;

        const pct = audio.duration ? (audio.currentTime / audio.duration) * 100 : 0;
        updateVisuals(pct);

        if (timeEl.current) {
            timeEl.current.textContent = formatTime(audio.currentTime);
        }

        rafId.current = requestAnimationFrame(tickFn);
    }, [updateVisuals]);

    const resetBars = useCallback(() => {
        updateVisuals(0);
        if (timeEl.current) {
            const d = durationProp
                ? (typeof durationProp === 'string' ? durationProp : formatTime(parseDuration(durationProp)))
                : formatTime(duration);
            timeEl.current.textContent = d;
        }
    }, [durationProp, duration, updateVisuals]);

    useEffect(() => {
        if (!audioRef.current) return;
        const audio = audioRef.current;

        const handlePlay = () => { rafId.current = requestAnimationFrame(tickFn); };
        const handlePause = () => { if (rafId.current) cancelAnimationFrame(rafId.current); };
        const handleLoadedMetadata = () => setDuration(audio.duration);
        const handleEnded = () => {
            if (rafId.current) cancelAnimationFrame(rafId.current);
            setIsPlaying(false);
            resetBars();
        };

        audio.addEventListener('play', handlePlay);
        audio.addEventListener('pause', handlePause);
        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        audio.addEventListener('ended', handleEnded);
        return () => {
            if (rafId.current) cancelAnimationFrame(rafId.current);
            audio.removeEventListener('play', handlePlay);
            audio.removeEventListener('pause', handlePause);
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
            audio.removeEventListener('ended', handleEnded);
        };
    }, [tickFn, resetBars]);

    const togglePlay = useCallback(() => {
        if (!audioRef.current) return;
        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
        }
        setIsPlaying(!isPlaying);
    }, [isPlaying]);

    const cycleSpeed = useCallback(() => {
        const nextIdx = (speedIdx + 1) % PLAYBACK_SPEEDS.length;
        setSpeedIdx(nextIdx);
        if (audioRef.current) {
            audioRef.current.playbackRate = PLAYBACK_SPEEDS[nextIdx];
        }
    }, [speedIdx]);

    const handleWaveformClick = useCallback((e) => {
        if (!audioRef.current?.duration) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const pct = (x / rect.width) * 100;
        audioRef.current.currentTime = (pct / 100) * audioRef.current.duration;
        updateVisuals(pct);
    }, [updateVisuals]);

    const handleTranscribe = useCallback(async () => {
        if (!onTranscribe || !messageId || transcribing) return;
        setTranscribing(true);
        setTranscribeError(null);
        try {
            await onTranscribe(messageId);
        } catch (err) {
            console.error('[AudioContent] Transcription failed:', err);
            setTranscribeError(err.message || 'Falha ao transcrever');
        } finally {
            setTranscribing(false);
        }
    }, [onTranscribe, messageId, transcribing]);

    const handleCopy = useCallback(async () => {
        if (!transcription || copied) return;
        try {
            await navigator.clipboard.writeText(transcription);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Fallback silencioso
        }
    }, [transcription, copied]);

    const displayDuration = typeof durationProp === 'string'
        ? durationProp
        : formatTime(durationProp ? parseDuration(durationProp) : duration);

    const speed = PLAYBACK_SPEEDS[speedIdx];

    return (
        <div className={cn("w-full min-w-[230px] max-w-[340px]", className)}>
            <div className="flex items-center gap-3 p-2.5 rounded-xl">
                <audio ref={audioRef} src={src} preload="metadata" className="hidden" />

                {/* Large green play button — WhatsApp style */}
                <button
                    className="h-11 w-11 shrink-0 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white flex items-center justify-center shadow-md transition-colors"
                    onClick={togglePlay}
                >
                    {isPlaying
                        ? <Pause className="h-5 w-5 fill-current" />
                        : <Play className="h-5 w-5 fill-current ml-0.5" />
                    }
                </button>

                {/* Waveform + speed + progress bar */}
                <div className="flex-1 min-w-0 flex flex-col gap-1">
                    {/* Waveform bars */}
                    <div
                        className="flex items-center gap-[1px] cursor-pointer h-[30px]"
                        onClick={handleWaveformClick}
                    >
                        {barHeights.map((h, i) => (
                            <div
                                key={i}
                                ref={el => { barEls.current[i] = el; }}
                                className="flex-1 rounded-sm"
                                style={{
                                    height: `${h}px`,
                                    minWidth: '2px',
                                    backgroundColor: isDark ? 'hsla(0, 0%, 100%, 0.22)' : 'hsla(0, 0%, 0%, 0.15)',
                                    willChange: 'background-color',
                                }}
                            />
                        ))}
                    </div>

                    {/* Bottom row: speed + progress bar */}
                    <div className="flex items-center gap-2">
                        {/* Speed badge */}
                        <button
                            onClick={cycleSpeed}
                            className={cn(
                                "text-[10px] font-semibold bg-emerald-500/15 rounded px-1.5 py-0.5 tabular-nums leading-none transition-colors shrink-0",
                                isDark ? "text-emerald-400 hover:text-emerald-300" : "text-emerald-600 hover:text-emerald-500"
                            )}
                        >
                            {speed % 1 === 0 ? `${speed}x` : `${speed}x`}
                        </button>

                        {/* Progress bar track */}
                        <div
                            className={cn("flex-1 h-[3px] rounded-full overflow-hidden cursor-pointer", isDark ? "bg-white/10" : "bg-black/10")}
                            onClick={handleWaveformClick}
                        >
                            <div
                                ref={progressEl}
                                className="h-full bg-emerald-500 rounded-full"
                                style={{ width: '0%', transition: 'none' }}
                            />
                        </div>

                        {/* Time */}
                        <span
                            ref={timeEl}
                            className="text-[10px] text-foreground-muted font-medium tabular-nums shrink-0"
                        >
                            {displayDuration}
                        </span>
                    </div>
                </div>
            </div>

            {transcription && transcription !== '__empty__' && (
                <div className="mt-1.5 px-1">
                    <div className="flex items-start gap-1.5">
                        <p className="text-xs text-foreground-muted italic border-l-2 border-cyan-600 pl-2 flex-1 min-w-0">
                            &ldquo;{transcription}&rdquo;
                        </p>
                        <button
                            onClick={handleCopy}
                            className="shrink-0 p-0.5 rounded text-foreground-muted hover:text-foreground transition-colors"
                            title={copied ? 'Copiado!' : 'Copiar transcrição'}
                        >
                            {copied
                                ? <Check className="h-3.5 w-3.5 text-emerald-500" />
                                : <Copy className="h-3.5 w-3.5" />
                            }
                        </button>
                    </div>
                </div>
            )}

            {transcription === '__empty__' && (
                <p className="mt-1 px-3 text-[11px] text-foreground-muted italic">
                    Nenhum conteúdo de fala identificado neste áudio.
                </p>
            )}

            {transcribing && (
                <div className="mt-1.5 px-1">
                    <div className="flex items-center gap-2 px-2 py-1.5">
                        <Loader2 className="h-3.5 w-3.5 animate-spin text-cyan-500" />
                        <span className="text-[11px] text-foreground-muted italic">Transcrevendo áudio…</span>
                    </div>
                </div>
            )}

            {!transcription && !transcribing && onTranscribe && messageId && (
                <div className="mt-1 px-1">
                    <button
                        onClick={handleTranscribe}
                        className="flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded-lg transition-colors text-cyan-600 dark:text-cyan-400 hover:bg-cyan-500/10"
                    >
                        <Mic className="h-3.5 w-3.5" /> Transcrever
                    </button>
                    {transcribeError && (
                        <p className="text-[11px] text-red-500 mt-0.5 px-2">{transcribeError}</p>
                    )}
                </div>
            )}
        </div>
    );
}

function formatTime(time) {
    if (Number.isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function parseDuration(str) {
    if (typeof str === 'number') return str;
    if (!str) return 0;
    const parts = String(str).split(':');
    if (parts.length === 2) return parseInt(parts[0]) * 60 + parseInt(parts[1]);
    return parseFloat(str) || 0;
}
